import React from 'react';
import { LineChart, BarChart } from '../../Charts';

const CloudflareMetrics = ({ data }) => {
  if (!data) {
    return <div className="metrics-loading">Loading Cloudflare data...</div>;
  }

  if (data.error) {
    return (
      <div className="section-error">
        <p>{data.error}</p>
        <div className="setup-instructions">
          <h4>Cloudflare Setup Instructions</h4>
          <p>To connect Cloudflare, add these environment variables:</p>
          <pre>
            {`REACT_APP_CLOUDFLARE_API_KEY=your_api_key
REACT_APP_CLOUDFLARE_ZONE_ID=your_zone_id
REACT_APP_CLOUDFLARE_EMAIL=your_email`}
          </pre>
          <p>You can get these values from your Cloudflare account dashboard.</p>
        </div>
      </div>
    );
  }

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Process traffic data for charts
  const prepareTrafficData = () => {
    if (!data.traffic || !data.traffic.timeseries) {
      return [];
    }
    
    return data.traffic.timeseries.map(point => ({
      time: new Date(point.since).toLocaleDateString(),
      requests: point.requests?.all || 0,
      bandwidth: (point.bandwidth?.all || 0) / (1024 * 1024) // Convert to MB
    }));
  };
  
  // Process threats data
  const prepareThreatsData = () => {
    if (!data.traffic || !data.traffic.timeseries) {
      return [];
    }
    
    return data.traffic.timeseries.map(point => ({
      time: new Date(point.since).toLocaleDateString(),
      threats: point.threats?.all || 0
    }));
  };
  
  const trafficData = prepareTrafficData();
  const threatsData = prepareThreatsData();
  
  // Calculate totals
  const calculateTotals = () => {
    if (!data.traffic || !data.traffic.totals) {
      return {
        requests: 0,
        bandwidth: 0,
        threats: 0,
        pageviews: 0
      };
    }
    
    const totals = data.traffic.totals;
    
    return {
      requests: totals.requests?.all || 0,
      bandwidth: (totals.bandwidth?.all || 0) / (1024 * 1024 * 1024), // Convert to GB
      threats: totals.threats?.all || 0,
      pageviews: totals.pageviews?.all || 0
    };
  };
  
  const totals = calculateTotals();

  return (
    <div className="cloudflare-metrics">
      <div className="metrics-grid">
        <div className="metric-card">
          <h4>Total Requests</h4>
          <p className="metric-value">{totals.requests.toLocaleString()}</p>
          <p className="metric-label">Last 30 Days</p>
        </div>
        
        <div className="metric-card">
          <h4>Bandwidth</h4>
          <p className="metric-value">{totals.bandwidth.toFixed(2)} GB</p>
          <p className="metric-label">Last 30 Days</p>
        </div>
        
        <div className="metric-card">
          <h4>Threats</h4>
          <p className="metric-value">{totals.threats.toLocaleString()}</p>
          <p className="metric-label">Blocked</p>
        </div>
        
        <div className="metric-card">
          <h4>Page Views</h4>
          <p className="metric-value">{totals.pageviews.toLocaleString()}</p>
          <p className="metric-label">Last 30 Days</p>
        </div>
      </div>
      
      {trafficData.length > 0 ? (
        <div className="charts-container">
          <div className="chart-wrapper">
            <h4>Traffic Over Time</h4>
            <LineChart 
              data={trafficData}
              xKey="time"
              yKey="requests"
              color="#60A5FA"
            />
          </div>
          
          <div className="chart-wrapper">
            <h4>Bandwidth Usage (MB)</h4>
            <LineChart 
              data={trafficData}
              xKey="time"
              yKey="bandwidth"
              color="#F59E0B"
            />
          </div>
        </div>
      ) : (
        <div className="no-data-message">
          <p>No Cloudflare traffic data available for the selected time period.</p>
        </div>
      )}
      
      {threatsData.length > 0 && threatsData.some(d => d.threats > 0) && (
        <div className="chart-wrapper">
          <h4>Security Threats</h4>
          <LineChart 
            data={threatsData}
            xKey="time"
            yKey="threats"
            color="#EF4444"
          />
        </div>
      )}
      
      {data.security && data.security.length > 0 && (
        <div className="security-events-table">
          <h4>Recent Security Events</h4>
          <table>
            <thead>
              <tr>
                <th>Time</th>
                <th>Country</th>
                <th>Action</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              {data.security.slice(0, 5).map((event, index) => (
                <tr key={index}>
                  <td>{new Date(event.occurred_at).toLocaleString()}</td>
                  <td>{event.country || 'Unknown'}</td>
                  <td>{event.action || 'Unknown'}</td>
                  <td>{event.source_ip || 'Unknown'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CloudflareMetrics; 